import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCockpitApi } from '@/api/provider';

const DEFAULT_STALE = 30_000;

export function useDrafts(opts: { includeApproved?: boolean } = {}) {
  const api = useCockpitApi();
  const includeApproved = opts.includeApproved === true;
  return useQuery({
    queryKey: ['cockpit', 'drafts', { includeApproved }],
    queryFn: () => api.drafts({ includeApproved }),
    staleTime: DEFAULT_STALE,
  });
}

export function useDraftApprove() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      requestedShippingDate: string | null;
      requestedDeliveryDate: string | null;
      shippingDateConfirmed: boolean;
      deliveryReviewRevision?: string;
      paymentTermsOverride?: string;
      paymentTermsOverrideReason?: string;
    }) =>
      api.approveDraft(input.id, {
        requestedShippingDate: input.requestedShippingDate,
        requestedDeliveryDate: input.requestedDeliveryDate,
        shippingDateConfirmed: input.shippingDateConfirmed,
        ...(input.deliveryReviewRevision ? { deliveryReviewRevision: input.deliveryReviewRevision } : {}),
        ...(input.paymentTermsOverride ? { paymentTermsOverride: input.paymentTermsOverride } : {}),
        ...(input.paymentTermsOverrideReason
          ? { paymentTermsOverrideReason: input.paymentTermsOverrideReason }
          : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'drafts'] });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'drafts'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'queue'] });
    },
  });
}

export function useDraftReject() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) => api.rejectDraft(input.id, input.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'drafts'] });
    },
  });
}
